import {
    signup,
    signupLookup,
    type LoginPrefill,
    type OrganizationChoice,
} from '@api/auth';
import { getPasswordSchema, validateEmail } from '@diagon-alley/common';
import {
    Anchor,
    Button,
    PasswordInput,
    Radio,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import type { FC } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthContainer } from '../AuthContainer';

// Reusing helper from LoginLanding or duplicating it if not exported
export function useDebouncedBusy(thresholdMs = 400) {
    const [busy, setBusy] = useState(false);
    const timerRef = useRef<number | null>(null);

    const start = useCallback(() => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setBusy(true), thresholdMs);
    }, [thresholdMs]);

    const stop = useCallback(() => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = null;
        setBusy(false);
    }, []);

    return { busy, start, stop };
}

/**
 * EMAIL → (EXISTING →) DETAILS.
 *
 * Typing an address that already has logins shows the organisations they are in. Pick one
 * and you are sent to log in there (email and organisation prefilled); pick "new" and the
 * details form opens with your name prefilled and a password of its own — every
 * organisation is a separate login row (email × organisation), never a shared one.
 */
type Step = 'EMAIL' | 'EXISTING' | 'DETAILS';

/** Radio value for "set up a new organisation" beside the existing ones. */
const NEW_ORGANIZATION = '__new__';

const PRIMARY = { backgroundColor: 'black', color: 'white' } as const;

export const SignupPage: FC = () => {
    const nav = useNavigate();

    const [step, setStep] = useState<Step>('EMAIL');

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    /** Optional for a first org (server derives one from the domain); required for a second. */
    const [organizationName, setOrganizationName] = useState('');

    const [existingOrgs, setExistingOrgs] = useState<OrganizationChoice[]>([]);
    const [choice, setChoice] = useState<string>(NEW_ORGANIZATION);

    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const { busy, start, stop } = useDebouncedBusy(400);

    const handleContinue = useCallback(async () => {
        setError(null);
        if (!validateEmail(email)) {
            setError('Please enter a valid work email');
            return;
        }

        start();
        setSubmitting(true);
        try {
            const res = await signupLookup(email);

            if (res.existing && res.organizations.length > 0) {
                setFirstName(res.firstName ?? '');
                setLastName(res.lastName ?? '');
                setExistingOrgs(res.organizations);
                setChoice(res.organizations[0]!.id);
                setStep('EXISTING');
                return;
            }

            setExistingOrgs([]);
            setStep('DETAILS');
        } catch (err: any) {
            setError(err?.error?.message || err?.message || 'Could not check this email');
        } finally {
            stop();
            setSubmitting(false);
        }
    }, [email, start, stop]);

    const handleChoice = useCallback(() => {
        setError(null);
        if (choice === NEW_ORGANIZATION) {
            setStep('DETAILS');
            return;
        }

        const prefill: LoginPrefill = { email, organizationId: choice };
        nav('/login', { state: prefill });
    }, [choice, email, nav]);

    const handleSubmit = useCallback(async () => {
        setError(null);

        if (!firstName || !lastName || !password) {
            setError('All fields are required');
            return;
        }

        if (existingOrgs.length > 0 && !organizationName.trim()) {
            setError('Name the new organisation so it can be told apart from your existing one');
            return;
        }

        if (!getPasswordSchema().safeParse(password).success) {
            setError('Password must be at least 8 characters and strong.');
            return;
        }

        start();
        setSubmitting(true);
        try {
            const res = await signup({
                firstName,
                lastName,
                email,
                password,
                organizationName: organizationName.trim() || undefined,
            });
            localStorage.setItem('token', res.accessToken.token);
            localStorage.setItem('user', JSON.stringify(res.user));
            nav('/home');
        } catch (err: any) {
            setError(err?.error?.message || err?.message || 'Signup failed');
        } finally {
            stop();
            setSubmitting(false);
        }
    }, [firstName, lastName, email, password, organizationName, existingOrgs, nav, start, stop]);

    const errorBox = error && (
        <div className="rounded-md bg-red-50 p-2 text-sm text-red-600">{error}</div>
    );

    return (
        <AuthContainer subtitle="Create your account">
            <Stack gap="md">
                {step === 'EMAIL' && (
                    <>
                        <Stack gap="xs">
                            <Text size="sm" fw={500}>
                                Work Email
                            </Text>
                            <TextInput
                                placeholder="you@company.com"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.currentTarget.value);
                                    setError(null);
                                }}
                                autoComplete="email"
                                error={error}
                            />
                            <Text size="xs" c="dimmed">
                                Already on Reparo? Use the same email — you can
                                open your existing organisation or set up another.
                            </Text>
                        </Stack>

                        <Button
                            onClick={handleContinue}
                            disabled={submitting || busy}
                            radius="md"
                            fullWidth
                            h={44}
                            style={PRIMARY}
                        >
                            {submitting || busy ? 'Checking...' : 'Continue'}
                        </Button>
                    </>
                )}

                {step === 'EXISTING' && (
                    <>
                        {errorBox}
                        <Anchor
                            component="button"
                            onClick={() => setStep('EMAIL')}
                            size="sm"
                            c="dimmed"
                            style={{ alignSelf: 'flex-start' }}
                        >
                            ← {email}
                        </Anchor>
                        <Radio.Group
                            label="This email already has an account"
                            description="Log in to one of your organisations, or set up a new one"
                            value={choice}
                            onChange={setChoice}
                        >
                            <Stack gap="xs" mt="xs">
                                {existingOrgs.map((org) => (
                                    <Radio key={org.id} value={org.id} label={org.name} />
                                ))}
                                <Radio
                                    value={NEW_ORGANIZATION}
                                    label="Set up a new organisation"
                                />
                            </Stack>
                        </Radio.Group>
                        <Button onClick={handleChoice} radius="md" fullWidth h={44} style={PRIMARY}>
                            {choice === NEW_ORGANIZATION ? 'Continue' : 'Log in'}
                        </Button>
                    </>
                )}

                {step === 'DETAILS' && (
                    <>
                        {errorBox}
                        <Anchor
                            component="button"
                            onClick={() =>
                                setStep(existingOrgs.length > 0 ? 'EXISTING' : 'EMAIL')
                            }
                            size="sm"
                            c="dimmed"
                            style={{ alignSelf: 'flex-start' }}
                        >
                            ← Back
                        </Anchor>

                        <Stack gap="xs">
                            <Text size="sm" fw={500}>
                                Organisation name
                            </Text>
                            <TextInput
                                placeholder={
                                    existingOrgs.length > 0
                                        ? 'e.g. Mokobara WAU'
                                        : 'Acme (optional — defaults to your email domain)'
                                }
                                value={organizationName}
                                onChange={(e) => setOrganizationName(e.currentTarget.value)}
                            />
                        </Stack>
                        <Stack gap="xs">
                            <Text size="sm" fw={500}>
                                First Name
                            </Text>
                            <TextInput
                                placeholder="John"
                                value={firstName}
                                onChange={(e) => setFirstName(e.currentTarget.value)}
                            />
                        </Stack>
                        <Stack gap="xs">
                            <Text size="sm" fw={500}>
                                Last Name
                            </Text>
                            <TextInput
                                placeholder="Doe"
                                value={lastName}
                                onChange={(e) => setLastName(e.currentTarget.value)}
                            />
                        </Stack>
                        <Stack gap="xs">
                            <Text size="sm" fw={500}>
                                Password
                            </Text>
                            <PasswordInput
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.currentTarget.value)}
                                autoComplete="new-password"
                            />
                            <Stack gap={2}>
                                <Text size="xs" c="dimmed">
                                    • At least 8 characters
                                </Text>
                                <Text size="xs" c="dimmed">
                                    • Include uppercase & lowercase
                                </Text>
                                <Text size="xs" c="dimmed">
                                    • Include at least one number
                                </Text>
                                {existingOrgs.length > 0 && (
                                    <Text size="xs" c="dimmed">
                                        • This is the password for the new organisation
                                        only — your existing login is unchanged
                                    </Text>
                                )}
                            </Stack>
                        </Stack>

                        <Button
                            onClick={handleSubmit}
                            disabled={submitting || busy}
                            radius="md"
                            fullWidth
                            h={44}
                            style={PRIMARY}
                        >
                            {submitting || busy ? 'Creating Account...' : 'Create Account'}
                        </Button>
                    </>
                )}

                <Text size="xs" c="dimmed" ta="center" mt="sm">
                    By continuing, you agree to our Terms of Service and Privacy
                    Policy
                </Text>
            </Stack>
        </AuthContainer>
    );
};
